import type { Map as MlMap } from 'maplibre-gl'

/**
 * Vehicle markers, drawn at runtime rather than taken from a sprite sheet.
 *
 * Three reasons. The basemap sprite is protomaps' and its contents are their business, not
 * a contract to depend on; `icon-color` only tints SDF icons, which these are not; and a
 * downloaded icon set would be an outbound dependency on a console whose entire tile stack
 * exists so a depot machine with no internet still works. Generating one opaque image per
 * (state x load) needs nothing external and renders crisply because it is drawn at the
 * device pixel ratio.
 *
 * The shape is a heavy goods vehicle seen from above, nose forward: steer axle, tandem
 * drive axles, cab with a windscreen and mirrors, then the deck. Heading is real
 * information on a corridor where the laden and empty runs share the same road, and a
 * dispatcher reads a silhouette faster than a legend. The axle count is not decoration --
 * it is what makes the shape read as a truck rather than as an arrow at 20 px.
 *
 * LOAD IS THE SECOND DIMENSION, and it is carried by fill rather than by hue. A laden
 * truck has a solid cargo deck; an empty one is hollow. Colour is already spoken for by
 * state, and a corridor where half the fleet is laden cannot afford to spend a second hue
 * on it -- the two would collide the moment a laden truck went idle.
 */

// Drawn big and scaled down by icon-size. Rasterising at the final on-screen size would
// pin the detail to whatever zoom happened to be current when the style loaded.
const W = 38
const H = 66

export type MarkerState = 'moving' | 'idle' | 'charging' | 'stale' | 'down'

export const MARKER_COLOR: Record<MarkerState, string> = {
  moving: '#16A34A',
  idle: '#D97706',
  charging: '#2563EB',
  stale: '#94A3B8',
  down: '#DC2626',
}

/** Tyres and glass, constant across states: a truck's wheels are not a status. */
const TYRE = '#1F2937'
const GLASS = 'rgba(255,255,255,0.85)'
const OUTLINE = 'rgba(255,255,255,0.94)'

function roundRect(
  g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2)
  g.beginPath()
  g.moveTo(x + rr, y)
  g.lineTo(x + w - rr, y)
  g.quadraticCurveTo(x + w, y, x + w, y + rr)
  g.lineTo(x + w, y + h - rr)
  g.quadraticCurveTo(x + w, y + h, x + w - rr, y + h)
  g.lineTo(x + rr, y + h)
  g.quadraticCurveTo(x, y + h, x, y + h - rr)
  g.lineTo(x, y + rr)
  g.quadraticCurveTo(x, y, x, y + rr)
  g.closePath()
}

/** One axle: a tyre either side of the hull, drawn under it so the hull caps them. */
function axle(g: CanvasRenderingContext2D, cy: number, halfTrack: number, len: number): void {
  const tw = 5
  g.fillStyle = TYRE
  roundRect(g, W / 2 - halfTrack - tw, cy - len / 2, tw, len, 1.8)
  g.fill()
  roundRect(g, W / 2 + halfTrack, cy - len / 2, tw, len, 1.8)
  g.fill()
}

/** Exported so a caller needing a truck in a colour outside MARKER_COLOR (the Global map's
 *  EIM charging teal -- see GlobalMap.tsx -- which deliberately differs from this file's
 *  own charging blue) can draw one without a second copy of the silhouette. */
export function truck(color: string, laden: boolean, ratio: number): ImageData {
  const c = document.createElement('canvas')
  c.width = W * ratio
  c.height = H * ratio
  const g = c.getContext('2d')!
  g.scale(ratio, ratio)

  const cx = W / 2
  g.lineJoin = 'round'
  g.lineCap = 'round'

  // ---- running gear, first so the hull overlaps the inner edge of every tyre
  axle(g, 17, 12, 9)          // steer
  axle(g, 40, 12.5, 9)        // drive, leading
  axle(g, 51, 12.5, 9)        // drive, trailing -- tandem reads as "heavy"
  const deckTop = 27

  // ---- cargo deck. Filled when laden, hollow when running empty.
  const bx = cx - 13
  const bw = 26
  const bh = H - 3 - deckTop
  roundRect(g, bx, deckTop, bw, bh, 3)
  if (laden) {
    g.fillStyle = color
    g.fill()
    g.strokeStyle = OUTLINE
    g.lineWidth = 1.8
    g.stroke()
    // Load bands, so "full" still reads where the marker is only a few pixels tall.
    g.strokeStyle = 'rgba(255,255,255,0.5)'
    g.lineWidth = 1.3
    g.beginPath()
    for (const t of [0.3, 0.55, 0.8]) {
      g.moveTo(bx + 3, deckTop + bh * t)
      g.lineTo(bx + bw - 3, deckTop + bh * t)
    }
    g.stroke()
  } else {
    g.fillStyle = 'rgba(255,255,255,0.94)'
    g.fill()
    g.strokeStyle = color
    g.lineWidth = 2.6
    g.stroke()
  }

  // ---- mirrors, before the cab so the cab body caps their inner ends
  g.fillStyle = TYRE
  roundRect(g, cx - 16.5, 9.5, 5, 2.6, 1.2)
  g.fill()
  roundRect(g, cx + 11.5, 9.5, 5, 2.6, 1.2)
  g.fill()

  // ---- cab. Always solid: the tractor is the vehicle, the deck is its payload.
  roundRect(g, cx - 12, 3, 24, 25, 4.5)
  g.fillStyle = color
  g.fill()
  g.strokeStyle = OUTLINE
  g.lineWidth = 1.8
  g.stroke()

  // Windscreen across the nose -- the one cue that says which end is the front.
  roundRect(g, cx - 8, 6.5, 16, 5.5, 2)
  g.fillStyle = GLASS
  g.fill()

  return g.getImageData(0, 0, W * ratio, H * ratio)
}

export function iconFor(state: MarkerState, laden: boolean): string {
  return `veh-${state}-${laden ? 'laden' : 'empty'}`
}

/** Idempotent: style reloads re-run this, and addImage throws on a duplicate id. */
export function registerVehicleIcons(map: MlMap): void {
  const ratio = Math.min(3, Math.max(1, Math.round(window.devicePixelRatio || 1)))
  for (const state of Object.keys(MARKER_COLOR) as MarkerState[]) {
    for (const laden of [true, false]) {
      const id = iconFor(state, laden)
      if (map.hasImage(id)) continue
      map.addImage(id, truck(MARKER_COLOR[state], laden, ratio), { pixelRatio: ratio })
    }
  }
}
